import { useRef, useState, useEffect, useCallback } from "react";
import { Stage, Layer, Line, Circle, Text, Rect, Group, Wedge } from "react-konva";
import { inCone, roomAt, coveredByReliable, samplePath, classifyPathSamples, dwellCenter } from "./geometry";

const PADDING = 40; // px padding around the floor plan

const DOOR_COLORS = {
  swing: "#ffd24a",
  sliding: "#4ad2ff",
  open: "#7CFC9E",
};

const DOOR_LABELS = {
  swing: "Swing door",
  sliding: "Sliding door",
  open: "Open passage",
};

function calculateArea(polygon) {
  let area = 0;
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += polygon[i].x * polygon[j].y;
    area -= polygon[j].x * polygon[i].y;
  }
  return Math.abs(area / 2);
}

function getCentroid(polygon) {
  let cx = 0, cy = 0;
  for (const p of polygon) {
    cx += p.x;
    cy += p.y;
  }
  return { x: cx / polygon.length, y: cy / polygon.length };
}

function edgeLength(p1, p2) {
  return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
}

function FloorPlanCanvas({
  floorPlan,
  editable = false,
  selectedRoomId,
  selectedDoorId,
  selectedStairId,
  selectedNodeId,
  selectedLightId,
  selectedDwellId,
  showGrid,
  showHeatmap,
  layers = {},
  drawingPath,
  draftPoints,
  selectedPathId,
  onRoomSelect,
  onVertexDrag,
  onEdgeDrag,
  onDoorSelect,
  onDoorDrag,
  onStairSelect,
  onStairDrag,
  onNodeSelect,
  onNodeDrag,
  onLightSelect,
  onLightDrag,
  onDwellSelect,
  onDwellDrag,
  onDwellResize,
  onPathSelect,
  onAddPathPoint,
  onPathPointDrag,
}) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, name: "", area: 0 });

  // Calculate pixels-per-meter based on container size
  const pxPerMeter = Math.min(
    (dimensions.width - PADDING * 2) / floorPlan.width,
    (dimensions.height - PADDING * 2) / floorPlan.height
  );

  // Convert meters to canvas pixels
  const toPixel = useCallback((meters) => meters * pxPerMeter, [pxPerMeter]);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Handle zoom with scroll wheel
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    const oldScale = scale;
    const pointer = stage.getPointerPosition();

    const scaleBy = 1.08;
    const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const clampedScale = Math.max(0.3, Math.min(5, newScale));

    const mousePointTo = {
      x: (pointer.x - stagePos.x) / oldScale,
      y: (pointer.y - stagePos.y) / oldScale,
    };

    setScale(clampedScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    });
  }, [scale, stagePos]);

  const handleDragEnd = useCallback((e) => {
    // Only update stage position if the Stage itself was dragged,
    // not a child (vertex/edge handle) whose drag event bubbled up.
    if (e.target === e.target.getStage()) {
      setStagePos({ x: e.target.x(), y: e.target.y() });
    }
  }, []);

  // Render grid
  const renderGrid = () => {
    if (!showGrid) return null;
    const lines = [];
    const gridSpacing = 1; // 1 meter
    const gridColor = "rgba(255,255,255,0.06)";

    for (let x = 0; x <= floorPlan.width; x += gridSpacing) {
      lines.push(
        <Line
          key={`gv-${x}`}
          points={[toPixel(x) + PADDING, PADDING, toPixel(x) + PADDING, toPixel(floorPlan.height) + PADDING]}
          stroke={gridColor}
          strokeWidth={0.5}
        />
      );
    }
    for (let y = 0; y <= floorPlan.height; y += gridSpacing) {
      lines.push(
        <Line
          key={`gh-${y}`}
          points={[PADDING, toPixel(y) + PADDING, toPixel(floorPlan.width) + PADDING, toPixel(y) + PADDING]}
          stroke={gridColor}
          strokeWidth={0.5}
        />
      );
    }
    return lines;
  };

  // Render rooms
  const renderRooms = () => {
    return floorPlan.rooms.map((room) => {
      const isSelected = room.id === selectedRoomId;
      const points = room.polygon.flatMap((p) => [toPixel(p.x) + PADDING, toPixel(p.y) + PADDING]);
      const centroid = getCentroid(room.polygon);
      const area = calculateArea(room.polygon);

      // Decide whether inline labels fit; otherwise rely on hover tooltip
      const xs = room.polygon.map((p) => p.x);
      const ys = room.polygon.map((p) => p.y);
      const pxW = toPixel(Math.max(...xs) - Math.min(...xs));
      const pxH = toPixel(Math.max(...ys) - Math.min(...ys));
      const nameW = room.name.length * 7; // approx px width of the bold label
      const fitsName = pxW > nameW + 8 && pxH > 26;
      const fitsArea = pxW > 54 && pxH > 44;

      const showTooltip = (e) => {
        const stage = e.target.getStage();
        stage.container().style.cursor = "pointer";
        const pos = stage.getPointerPosition();
        setTooltip({ visible: true, x: pos.x, y: pos.y, name: room.name, area });
      };
      const moveTooltip = (e) => {
        const pos = e.target.getStage().getPointerPosition();
        setTooltip((t) => (t.visible ? { ...t, x: pos.x, y: pos.y } : t));
      };
      const hideTooltip = (e) => {
        e.target.getStage().container().style.cursor = "default";
        setTooltip((t) => ({ ...t, visible: false }));
      };

      return (
        <Group key={room.id}>
          {/* Room fill */}
          <Line
            points={points}
            closed
            fill={room.color + "40"}
            stroke={isSelected ? "#ffffff" : room.color}
            strokeWidth={isSelected ? 2.5 : 1.5}
            onClick={() => onRoomSelect(room.id)}
            onTap={() => onRoomSelect(room.id)}
            onMouseEnter={showTooltip}
            onMouseMove={moveTooltip}
            onMouseLeave={hideTooltip}
            draggable={editable && isSelected}
            onDragStart={hideTooltip}
            onDragEnd={(e) => {
              // Translate the whole polygon by the drag delta (in meters)
              const dxM = e.target.x() / pxPerMeter;
              const dyM = e.target.y() / pxPerMeter;
              // Reset the Line's own offset (we bake the delta into the polygon)
              e.target.x(0);
              e.target.y(0);
              const newPolygon = room.polygon.map((p) => ({
                x: Math.round((p.x + dxM) * 10) / 10,
                y: Math.round((p.y + dyM) * 10) / 10,
              }));
              onEdgeDrag(room.id, newPolygon);
            }}
          />

          {/* Room name label — only when it fits, else hover tooltip */}
          {fitsName && (
            <Text
              x={toPixel(centroid.x) + PADDING}
              y={toPixel(centroid.y) + PADDING - (fitsArea ? 12 : 6)}
              text={room.name}
              fontSize={13}
              fontStyle="bold"
              fill="#ffffff"
              align="center"
              listening={false}
              offsetX={room.name.length * 3.5}
            />
          )}

          {/* Area label — only when it fits */}
          {fitsArea && (
            <Text
              x={toPixel(centroid.x) + PADDING}
              y={toPixel(centroid.y) + PADDING + 4}
              text={`${area.toFixed(1)} m²`}
              fontSize={11}
              fill="rgba(255,255,255,0.7)"
              align="center"
              listening={false}
              offsetX={25}
            />
          )}

          {/* Edge dimension labels (perimeter — only on the selected room) */}
          {isSelected &&
            room.polygon.map((p, i) => {
            const next = room.polygon[(i + 1) % room.polygon.length];
            const len = edgeLength(p, next);
            if (len < 0.5) return null;
            const midX = (p.x + next.x) / 2;
            const midY = (p.y + next.y) / 2;
            return (
              <Text
                key={`${room.id}-edge-${i}`}
                x={toPixel(midX) + PADDING}
                y={toPixel(midY) + PADDING - 8}
                text={`${len.toFixed(1)}m`}
                fontSize={9}
                fill="rgba(255,255,255,0.5)"
                align="center"
                listening={false}
                offsetX={15}
              />
            );
          })}

          {/* Draggable vertices (only when selected) */}
          {editable && isSelected &&
            room.polygon.map((p, i) => (
              <Circle
                key={`${room.id}-v-${i}`}
                x={toPixel(p.x) + PADDING}
                y={toPixel(p.y) + PADDING}
                radius={6}
                fill="#ffffff"
                stroke={room.color}
                strokeWidth={2}
                draggable
                onDragEnd={(e) => {
                  const newX = (e.target.x() - PADDING) / pxPerMeter;
                  const newY = (e.target.y() - PADDING) / pxPerMeter;
                  const snappedX = Math.round(newX * 10) / 10;
                  const snappedY = Math.round(newY * 10) / 10;
                  e.target.x(toPixel(snappedX) + PADDING);
                  e.target.y(toPixel(snappedY) + PADDING);
                  onVertexDrag(room.id, i, { x: snappedX, y: snappedY });
                }}
              />
            ))}

          {/* Draggable edge midpoints for uniform resize (only when selected) */}
          {editable && isSelected &&
            room.polygon.map((p, i) => {
              const next = room.polygon[(i + 1) % room.polygon.length];
              const midX = (p.x + next.x) / 2;
              const midY = (p.y + next.y) / 2;
              const len = edgeLength(p, next);
              if (len < 0.3) return null;
              return (
                <Rect
                  key={`${room.id}-edge-handle-${i}`}
                  x={toPixel(midX) + PADDING - 5}
                  y={toPixel(midY) + PADDING - 5}
                  width={10}
                  height={10}
                  fill={room.color}
                  stroke="#ffffff"
                  strokeWidth={1}
                  cornerRadius={2}
                  draggable
                  onDragEnd={(e) => {
                    const ex = (e.target.x() + 5 - PADDING) / pxPerMeter;
                    const ey = (e.target.y() + 5 - PADDING) / pxPerMeter;
                    // Determine drag direction: move edge perpendicular
                    const dx = next.x - p.x;
                    const dy = next.y - p.y;
                    const isHorizontal = Math.abs(dx) > Math.abs(dy);
                    const nextIdx = (i + 1) % room.polygon.length;

                    let newP, newNext;
                    if (isHorizontal) {
                      // Horizontal edge: drag moves it vertically
                      const snappedY = Math.round(ey * 10) / 10;
                      newP = { x: p.x, y: snappedY };
                      newNext = { x: next.x, y: snappedY };
                    } else {
                      // Vertical edge: drag moves it horizontally
                      const snappedX = Math.round(ex * 10) / 10;
                      newP = { x: snappedX, y: p.y };
                      newNext = { x: snappedX, y: next.y };
                    }

                    // Update both vertices at once
                    const newPolygon = [...room.polygon];
                    newPolygon[i] = newP;
                    newPolygon[nextIdx] = newNext;
                    // Reset handle position
                    const newMidX = (newP.x + newNext.x) / 2;
                    const newMidY = (newP.y + newNext.y) / 2;
                    e.target.x(toPixel(newMidX) + PADDING - 5);
                    e.target.y(toPixel(newMidY) + PADDING - 5);
                    onEdgeDrag(room.id, newPolygon);
                  }}
                />
              );
            })}
        </Group>
      );
    });
  };

  // Render doors / passages
  const renderDoors = () => {
    const doors = floorPlan.doors || [];
    return doors.map((door) => {
      const isSelected = door.id === selectedDoorId;
      const cx = toPixel(door.x) + PADDING;
      const cy = toPixel(door.y) + PADDING;
      const w = toPixel(door.width);
      const half = w / 2;
      const color = DOOR_COLORS[door.type] || "#ffffff";
      const sw = isSelected ? 3 : 2;

      // Shapes are drawn in the door's local frame (opening along the X axis,
      // centered at origin) and the Group applies position + rotation.
      const shapes = [];

      // Threshold line (the opening itself) for every door type
      shapes.push(
        <Line
          key="threshold"
          points={[-half, 0, half, 0]}
          stroke={color}
          strokeWidth={sw}
          dash={door.type === "open" ? [4, 4] : undefined}
          opacity={door.type === "open" ? 0.6 : 0.9}
        />
      );
      // Jambs (wall ends) on both sides
      shapes.push(
        <Line key="jambL" points={[-half, -4, -half, 4]} stroke={color} strokeWidth={sw} />,
        <Line key="jambR" points={[half, -4, half, 4]} stroke={color} strokeWidth={sw} />
      );

      if (door.type === "swing") {
        // Leaf (open position) + swing arc; hinge on the left jamb.
        shapes.push(
          <Line key="leaf" points={[-half, 0, -half, -w]} stroke={color} strokeWidth={sw} />
        );
        const arc = [];
        for (let a = 0; a <= 90; a += 9) {
          const r = (a * Math.PI) / 180;
          arc.push(-half + w * Math.cos(r), -w * Math.sin(r));
        }
        shapes.push(
          <Line key="arc" points={arc} stroke={color} strokeWidth={1.5} dash={[3, 3]} opacity={0.7} />
        );
      } else if (door.type === "sliding") {
        // Two overlapping sliding leaves offset from the threshold.
        shapes.push(
          <Line key="leaf1" points={[-half, -3, 1, -3]} stroke={color} strokeWidth={sw + 1} />,
          <Line key="leaf2" points={[-1, 3, half, 3]} stroke={color} strokeWidth={sw + 1} />
        );
      }

      return (
        <Group
          key={door.id}
          x={cx}
          y={cy}
          rotation={door.angle || 0}
          draggable={editable}
          onClick={() => onDoorSelect(door.id)}
          onTap={() => onDoorSelect(door.id)}
          onMouseEnter={(e) => {
            e.target.getStage().container().style.cursor = editable ? "move" : "pointer";
          }}
          onMouseLeave={(e) => {
            e.target.getStage().container().style.cursor = "default";
          }}
          onDragEnd={(e) => {
            const nx = (e.target.x() - PADDING) / pxPerMeter;
            const ny = (e.target.y() - PADDING) / pxPerMeter;
            onDoorDrag(door.id, {
              x: Math.round(nx * 10) / 10,
              y: Math.round(ny * 10) / 10,
            });
          }}
        >
          {/* Invisible hit area so the whole door is easy to grab */}
          <Rect
            x={-half - 6}
            y={-Math.max(w, 14) - 2}
            width={w + 12}
            height={Math.max(w, 14) + 10}
            fill="rgba(0,0,0,0.01)"
          />
          {isSelected && (
            <Circle radius={Math.max(half, 8) + 6} stroke="#ffffff" strokeWidth={1} dash={[4, 4]} opacity={0.6} />
          )}
          {shapes}
        </Group>
      );
    });
  };

  // Render staircases
  const renderStairs = () => {
    const stairs = floorPlan.stairs || [];
    return stairs.map((s) => {
      const isSelected = s.id === selectedStairId;
      const cx = toPixel(s.x) + PADDING;
      const cy = toPixel(s.y) + PADDING;
      const w = toPixel(s.width);
      const L = toPixel(s.length);
      const color = "#c08cff";
      const sw = isSelected ? 2.5 : 1.5;
      const dir = s.direction === "down" ? 1 : -1; // up = arrow toward top (-y)
      const shapes = [];

      // Outline
      shapes.push(
        <Rect key="outline" x={-w / 2} y={-L / 2} width={w} height={L} stroke={color} strokeWidth={sw} fill={color + "22"} />
      );
      // Step treads
      const numSteps = Math.max(2, Math.min(40, Math.round(s.length / 0.28)));
      for (let i = 1; i < numSteps; i++) {
        const yy = -L / 2 + (L * i) / numSteps;
        shapes.push(
          <Line key={`step-${i}`} points={[-w / 2, yy, w / 2, yy]} stroke={color} strokeWidth={1} opacity={0.6} />
        );
      }
      // Direction arrow along the centre line
      const tailY = -dir * (L / 2 - 6);
      const headY = dir * (L / 2 - 6);
      const ah = 6;
      shapes.push(
        <Line key="arrow" points={[0, tailY, 0, headY]} stroke={color} strokeWidth={2} />,
        <Line
          key="arrowhead"
          points={[-ah, headY - dir * ah * 1.3, 0, headY, ah, headY - dir * ah * 1.3]}
          stroke={color}
          strokeWidth={2}
        />
      );
      // UP / DN label in a corner
      shapes.push(
        <Text
          key="label"
          x={-w / 2 + 3}
          y={-L / 2 + 3}
          text={s.direction === "down" ? "DN" : "UP"}
          fontSize={10}
          fontStyle="bold"
          fill={color}
          listening={false}
        />
      );

      return (
        <Group
          key={s.id}
          x={cx}
          y={cy}
          rotation={s.angle || 0}
          draggable={editable}
          onClick={() => onStairSelect(s.id)}
          onTap={() => onStairSelect(s.id)}
          onMouseEnter={(e) => {
            e.target.getStage().container().style.cursor = editable ? "move" : "pointer";
          }}
          onMouseLeave={(e) => {
            e.target.getStage().container().style.cursor = "default";
          }}
          onDragEnd={(e) => {
            const nx = (e.target.x() - PADDING) / pxPerMeter;
            const ny = (e.target.y() - PADDING) / pxPerMeter;
            onStairDrag(s.id, {
              x: Math.round(nx * 10) / 10,
              y: Math.round(ny * 10) / 10,
            });
          }}
        >
          {isSelected && (
            <Rect
              x={-w / 2 - 5}
              y={-L / 2 - 5}
              width={w + 10}
              height={L + 10}
              stroke="#ffffff"
              strokeWidth={1}
              dash={[4, 4]}
              opacity={0.6}
            />
          )}
          {shapes}
        </Group>
      );
    });
  };

  // Render coverage heatmap (covered = green, blind = red), only over room interiors.
  const renderHeatmap = () => {
    if (!showHeatmap) return null;
    const nodes = floorPlan.nodes || [];
    const rooms = floorPlan.rooms || [];
    // Adaptive cell size to keep the cell count bounded.
    let cell = 0.25;
    const maxCells = 3500;
    while ((floorPlan.width / cell) * (floorPlan.height / cell) > maxCells) cell += 0.05;
    const cellPx = toPixel(cell);
    const rects = [];
    for (let x = 0; x < floorPlan.width; x += cell) {
      for (let y = 0; y < floorPlan.height; y += cell) {
        const cxm = x + cell / 2;
        const cym = y + cell / 2;
        if (!roomAt(cxm, cym, rooms)) continue; // only paint inside rooms
        let covered = false;
        for (const node of nodes) {
          for (const s of node.sensors || []) {
            if (inCone({ x: cxm, y: cym }, node, s.rotation, s.fovAngle, s.rangeM)) {
              covered = true;
              break;
            }
          }
          if (!covered && node.camera) {
            const c = node.camera;
            if (inCone({ x: cxm, y: cym }, node, c.rotation, c.fovAngle, c.rangeM)) covered = true;
          }
          if (covered) break;
        }
        rects.push(
          <Rect
            key={`hm-${x.toFixed(2)}-${y.toFixed(2)}`}
            x={toPixel(x) + PADDING}
            y={toPixel(y) + PADDING}
            width={cellPx}
            height={cellPx}
            fill={covered ? "#2ecc71" : "#e74c3c"}
            opacity={0.16}
            listening={false}
          />
        );
      }
    }
    return rects;
  };

  // Render sensing nodes (sensor cones + optional camera wedge).
  const renderNodes = () => {
    const nodes = floorPlan.nodes || [];
    const SENSOR_COLOR = "#2ec7b8";
    const CAMERA_COLOR = "#ffb454";
    return nodes.map((node) => {
      const isSelected = node.id === selectedNodeId;
      const cx = toPixel(node.x) + PADDING;
      const cy = toPixel(node.y) + PADDING;
      const sensors = node.sensors || [];
      return (
        <Group
          key={node.id}
          x={cx}
          y={cy}
          draggable={editable}
          onClick={() => onNodeSelect(node.id)}
          onTap={() => onNodeSelect(node.id)}
          onMouseEnter={(e) => {
            e.target.getStage().container().style.cursor = editable ? "move" : "pointer";
          }}
          onMouseLeave={(e) => {
            e.target.getStage().container().style.cursor = "default";
          }}
          onDragEnd={(e) => {
            const nx = (e.target.x() - PADDING) / pxPerMeter;
            const ny = (e.target.y() - PADDING) / pxPerMeter;
            onNodeDrag(node.id, {
              x: Math.round(nx * 10) / 10,
              y: Math.round(ny * 10) / 10,
            });
          }}
        >
          {/* Sensor cones: presence range + brighter inner reliable ring */}
          {sensors.map((s) => (
            <Group key={s.id}>
              <Wedge
                radius={toPixel(s.rangeM)}
                angle={s.fovAngle}
                rotation={s.rotation - s.fovAngle / 2}
                fill={SENSOR_COLOR}
                opacity={0.1}
                stroke={SENSOR_COLOR}
                strokeWidth={isSelected ? 1 : 0.5}
                listening={false}
              />
              <Wedge
                radius={toPixel(s.reliableM)}
                angle={s.fovAngle}
                rotation={s.rotation - s.fovAngle / 2}
                fill={SENSOR_COLOR}
                opacity={0.18}
                listening={false}
              />
            </Group>
          ))}

          {/* Camera FOV wedge */}
          {node.camera && (
            <Wedge
              radius={toPixel(node.camera.rangeM)}
              angle={node.camera.fovAngle}
              rotation={node.camera.rotation - node.camera.fovAngle / 2}
              fill={CAMERA_COLOR}
              opacity={0.1}
              stroke={CAMERA_COLOR}
              strokeWidth={0.5}
              listening={false}
            />
          )}

          {isSelected && (
            <Circle radius={13} stroke="#ffffff" strokeWidth={1} dash={[4, 4]} opacity={0.7} />
          )}

          {/* Node body */}
          <Circle radius={6} fill="#10302e" stroke={SENSOR_COLOR} strokeWidth={2} />
          {/* Unlinked nodes (no hardware MAC) get an amber warning ring */}
          {!node.mac && (
            <Circle radius={9} stroke="#ffb454" strokeWidth={1.5} dash={[3, 3]} listening={false} />
          )}
          {node.hasCamera && <Circle radius={2.5} fill={CAMERA_COLOR} listening={false} />}
          {node.hasLux && (
            <Circle x={9} y={-9} radius={3.5} fill="#f5d000" stroke="#7a6500" strokeWidth={1} listening={false} />
          )}

          <Text
            x={0}
            y={11}
            text={node.label || "Node"}
            fontSize={10}
            fill="#ffffff"
            align="center"
            listening={false}
            offsetX={(node.label || "Node").length * 2.6}
          />
        </Group>
      );
    });
  };

  // Render WiZ light fixtures (one marker per lamp, ×N badge for bulb count).
  const renderLights = () => {
    const lights = floorPlan.lights || [];
    return lights.map((l) => {
      const isSelected = l.id === selectedLightId;
      const cx = toPixel(l.x) + PADDING;
      const cy = toPixel(l.y) + PADDING;
      const devices = l.bulbs || [];
      const count = devices.length;
      // A fixture is "plug-driven" when it has devices and every one is a
      // smart plug (non-smart light: on/off only, no color/themes).
      const isPlug = count > 0 && devices.every((b) => b.kind === "plug");
      const fill = isPlug ? "#7c6cff" : "#f5d000";
      const stroke = isPlug ? "#3a2f8c" : "#7a6500";
      return (
        <Group
          key={l.id}
          x={cx}
          y={cy}
          draggable={editable}
          onClick={() => onLightSelect(l.id)}
          onTap={() => onLightSelect(l.id)}
          onMouseEnter={(e) => {
            e.target.getStage().container().style.cursor = editable ? "move" : "pointer";
          }}
          onMouseLeave={(e) => {
            e.target.getStage().container().style.cursor = "default";
          }}
          onDragEnd={(e) => {
            const nx = (e.target.x() - PADDING) / pxPerMeter;
            const ny = (e.target.y() - PADDING) / pxPerMeter;
            onLightDrag(l.id, {
              x: Math.round(nx * 10) / 10,
              y: Math.round(ny * 10) / 10,
            });
          }}
        >
          {isSelected && (
            <Circle radius={12} stroke="#ffffff" strokeWidth={1} dash={[4, 4]} opacity={0.7} />
          )}
          <Circle radius={7} fill={fill} stroke={stroke} strokeWidth={1.5} />
          {count > 0 && (
            <Text
              x={7}
              y={-13}
              text={`×${count}`}
              fontSize={10}
              fontStyle="bold"
              fill={fill}
              listening={false}
            />
          )}
          <Text
            x={0}
            y={11}
            text={l.label || "Light"}
            fontSize={10}
            fill="#ffffff"
            align="center"
            listening={false}
            offsetX={(l.label || "Light").length * 2.6}
          />
        </Group>
      );
    });
  };

  // Render dwell zones (resting places / furniture). Colored by whether their
  // center sits inside a sensor's reliable ring (~2 m for a seated person).
  const renderDwellZones = () => {
    const zones = floorPlan.dwellZones || [];
    const nodes = floorPlan.nodes || [];
    return zones.map((z) => {
      const isSelected = z.id === selectedDwellId;
      const w = toPixel(z.w || 1);
      const h = toPixel(z.h || 1);
      const cx = toPixel(z.x) + PADDING;
      const cy = toPixel(z.y) + PADDING;
      const covered = coveredByReliable(dwellCenter(z), nodes);
      const color = covered ? "#2ecc71" : "#e0884a";
      return (
        <Group
          key={z.id}
          x={cx}
          y={cy}
          draggable={editable}
          onClick={() => onDwellSelect(z.id)}
          onTap={() => onDwellSelect(z.id)}
          onMouseEnter={(e) => {
            e.target.getStage().container().style.cursor = editable ? "move" : "pointer";
          }}
          onMouseLeave={(e) => {
            e.target.getStage().container().style.cursor = "default";
          }}
          onDragEnd={(e) => {
            const nx = (e.target.x() - PADDING) / pxPerMeter;
            const ny = (e.target.y() - PADDING) / pxPerMeter;
            onDwellDrag(z.id, {
              x: Math.round(nx * 100) / 100,
              y: Math.round(ny * 100) / 100,
            });
          }}
        >
          <Rect
            width={w}
            height={h}
            cornerRadius={4}
            fill={color + "22"}
            stroke={color}
            strokeWidth={isSelected ? 2 : 1.25}
            dash={covered ? undefined : [5, 4]}
          />
          {/* Seat center marker (the point actually tested against the ring) */}
          <Circle x={w / 2} y={h / 2} radius={2.5} fill={color} listening={false} />
          <Text
            x={4}
            y={4}
            text={z.label || "Seat"}
            fontSize={10}
            fontStyle="bold"
            fill="#ffffff"
            listening={false}
          />
          {/* Bottom-right resize handle (only when selected) */}
          {editable && isSelected && (
            <Circle
              x={w}
              y={h}
              radius={5}
              fill="#ffffff"
              stroke={color}
              strokeWidth={1.5}
              draggable
              onMouseEnter={(e) => {
                e.target.getStage().container().style.cursor = "nwse-resize";
                e.cancelBubble = true;
              }}
              onMouseLeave={(e) => {
                e.target.getStage().container().style.cursor = "default";
              }}
              onDragStart={(e) => {
                e.cancelBubble = true;
              }}
              onDragMove={(e) => {
                e.cancelBubble = true;
              }}
              onDragEnd={(e) => {
                e.cancelBubble = true;
                const nw = Math.max(0.2, e.target.x() / pxPerMeter);
                const nh = Math.max(0.2, e.target.y() / pxPerMeter);
                const snapped = {
                  w: Math.round(nw * 100) / 100,
                  h: Math.round(nh * 100) / 100,
                };
                e.target.x(toPixel(snapped.w));
                e.target.y(toPixel(snapped.h));
                onDwellResize && onDwellResize(z.id, snapped);
              }}
            />
          )}
        </Group>
      );
    });
  };

  // Render walk-paths, coloring each sub-segment by sensor coverage.
  const renderPaths = () => {
    const paths = floorPlan.paths || [];
    const nodes = floorPlan.nodes || [];
    const out = [];
    paths.forEach((path) => {
      const pts = path.points || [];
      if (pts.length < 1) return;
      const isSelected = path.id === selectedPathId;
      const bidirectional = path.bidirectional !== false;
      const samples = samplePath(pts, 0.25);
      const { classes } = classifyPathSamples(samples, nodes, bidirectional);
      const COLORS = { covered: "#2ecc71", leadin: "#f0a030", blind: "#e74c3c" };
      // Colored coverage segments.
      for (let i = 0; i < samples.length - 1; i++) {
        const a = samples[i];
        const b = samples[i + 1];
        out.push(
          <Line
            key={`p-${path.id}-${i}`}
            points={[
              toPixel(a.x) + PADDING,
              toPixel(a.y) + PADDING,
              toPixel(b.x) + PADDING,
              toPixel(b.y) + PADDING,
            ]}
            stroke={COLORS[classes[i]] || "#e74c3c"}
            strokeWidth={isSelected ? 4 : 3}
            lineCap="round"
            onClick={() => onPathSelect && onPathSelect(path.id)}
            onTap={() => onPathSelect && onPathSelect(path.id)}
          />
        );
      }
      // Direction arrowhead(s): always at the end; also at the start when bidirectional.
      const arrowAt = (from, to, key) => {
        const ax = toPixel(from.x) + PADDING;
        const ay = toPixel(from.y) + PADDING;
        const bx = toPixel(to.x) + PADDING;
        const by = toPixel(to.y) + PADDING;
        const ang = Math.atan2(by - ay, bx - ax);
        const len = 11;
        const spread = 0.5;
        out.push(
          <Line
            key={key}
            points={[
              bx - len * Math.cos(ang - spread),
              by - len * Math.sin(ang - spread),
              bx,
              by,
              bx - len * Math.cos(ang + spread),
              by - len * Math.sin(ang + spread),
            ]}
            stroke="#ffffff"
            strokeWidth={isSelected ? 2.5 : 2}
            lineCap="round"
            lineJoin="round"
            listening={false}
          />
        );
      };
      if (pts.length >= 2) {
        arrowAt(pts[pts.length - 2], pts[pts.length - 1], `parrow-end-${path.id}`);
        if (bidirectional) arrowAt(pts[1], pts[0], `parrow-start-${path.id}`);
      }
      // Vertex handles.
      pts.forEach((p, i) => {
        out.push(
          <Circle
            key={`pv-${path.id}-${i}`}
            x={toPixel(p.x) + PADDING}
            y={toPixel(p.y) + PADDING}
            radius={isSelected ? 5 : 3}
            fill="#ffffff"
            stroke="#444"
            strokeWidth={1}
            draggable={editable}
            onClick={() => onPathSelect && onPathSelect(path.id)}
            onTap={() => onPathSelect && onPathSelect(path.id)}
            onDragStart={() => onPathSelect && onPathSelect(path.id)}
            onMouseEnter={(e) => {
              e.target.getStage().container().style.cursor = editable ? "move" : "pointer";
            }}
            onMouseLeave={(e) => {
              e.target.getStage().container().style.cursor = "default";
            }}
            onDragEnd={(e) => {
              const nx = (e.target.x() - PADDING) / pxPerMeter;
              const ny = (e.target.y() - PADDING) / pxPerMeter;
              const snapped = {
                x: Math.round(nx * 100) / 100,
                y: Math.round(ny * 100) / 100,
              };
              e.target.x(toPixel(snapped.x) + PADDING);
              e.target.y(toPixel(snapped.y) + PADDING);
              onPathPointDrag && onPathPointDrag(path.id, i, snapped);
            }}
          />
        );
      });
    });
    return out;
  };

  // Render the in-progress path being drawn.
  const renderDraft = () => {
    if (!drawingPath || !draftPoints || draftPoints.length === 0) return null;
    const linePts = [];
    draftPoints.forEach((p) => {
      linePts.push(toPixel(p.x) + PADDING, toPixel(p.y) + PADDING);
    });
    return (
      <>
        {draftPoints.length > 1 && (
          <Line points={linePts} stroke="#ffd24a" strokeWidth={2} dash={[6, 4]} lineCap="round" listening={false} />
        )}
        {draftPoints.map((p, i) => (
          <Circle
            key={`draft-${i}`}
            x={toPixel(p.x) + PADDING}
            y={toPixel(p.y) + PADDING}
            radius={4}
            fill="#ffd24a"
            listening={false}
          />
        ))}
      </>
    );
  };

  return (
    <div ref={containerRef} className="floor-plan-canvas">
      <Stage
        width={dimensions.width}
        height={dimensions.height}
        scaleX={scale}
        scaleY={scale}
        x={stagePos.x}
        y={stagePos.y}
        draggable={!drawingPath}
        onWheel={handleWheel}
        onDragEnd={handleDragEnd}
        onClick={(e) => {
          if (!drawingPath || !onAddPathPoint) return;
          const stage = e.target.getStage();
          const pos = stage.getRelativePointerPosition();
          const mx = (pos.x - PADDING) / pxPerMeter;
          const my = (pos.y - PADDING) / pxPerMeter;
          onAddPathPoint({
            x: Math.round(mx * 100) / 100,
            y: Math.round(my * 100) / 100,
          });
        }}
      >
        <Layer>
          {/* Background */}
          <Rect
            x={PADDING}
            y={PADDING}
            width={toPixel(floorPlan.width)}
            height={toPixel(floorPlan.height)}
            fill="#1a1a2e"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth={1}
          />

          {/* Grid */}
          {renderGrid()}

          {/* Rooms */}
          {layers.structure !== false && renderRooms()}

          {/* Doors / passages */}
          {layers.structure !== false && renderDoors()}

          {/* Staircases */}
          {layers.structure !== false && renderStairs()}

          {/* Coverage heatmap */}
          {renderHeatmap()}

          {/* Sensing nodes */}
          {layers.nodes !== false && renderNodes()}

          {/* Light fixtures */}
          {layers.lights !== false && renderLights()}

          {/* Resting places / furniture */}
          {layers.dwell !== false && renderDwellZones()}

          {/* Walk-paths */}
          {layers.paths !== false && renderPaths()}
          {renderDraft()}
        </Layer>
      </Stage>

      {tooltip.visible && (
        <div
          className="room-tooltip"
          style={{ left: tooltip.x + 14, top: tooltip.y + 14 }}
        >
          <span className="room-tooltip-name">{tooltip.name}</span>
          <span className="room-tooltip-area">{tooltip.area.toFixed(1)} m²</span>
        </div>
      )}
    </div>
  );
}

export default FloorPlanCanvas;
